// ─────────────────────────────────────────────────────────────────────────────
// InstructionsSection — Site Instructions register.
// Construction Engineering Cockpit aesthetic. Token-driven.
// Logic preserved from original; presentation rebuilt on ./ui.jsx primitives.
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useMemo, useRef, useState } from "react";
import { tokens } from "./theme";
import {
  Card, BlueprintGrid, Reveal, Eyebrow,
  Input, Select, Textarea, FormRow, FormGroupDivider,
  Button, Badge,
  Stat,
  Table, Th, Td,
  Empty,
} from "./ui";
import { requiresSignoff, approvalBadge, approveDocument } from "../utils/approvals";
import InstructionFormatPanel, {
  CustomFieldsInputs, getInstructionConfig, printInstruction,
  printInstructionRegister, SI_STATUS_OPTIONS, suggestNextSiNumber,
} from "./InstructionFormatPanel";
import VersionHistory from "./VersionHistory";
import PopulateTemplateModal from "./PopulateTemplateModal";
import { scanInstructionDocument } from "../utils/aiScan";
import { useDebouncedValue } from "./useDebouncedValue";

const SORT_OPTIONS = [
  { v: "instructionDate", l: "Issue date" },
  { v: "siNumber", l: "SI number" },
  { v: "subject", l: "Subject" },
  { v: "contractor", l: "Contractor" },
  { v: "status", l: "Status" },
];

// Human labels for the fields the scanner can fill — used in the review banner.
const SCAN_FIELD_LABELS = {
  siNumber: "SI Number", projectNumber: "Project Number", contractNumber: "Contract Number",
  contractDescription: "Contract Description", contractor: "Contractor", issuedBy: "Issued By",
  subject: "Subject", instructionText: "Instruction", drawingRevisionRef: "Drawing / Revision Ref",
  applicablePayItems: "Applicable Pay Items", asBuiltImplications: "As Built Implications",
  financialImplications: "Financial Implications", valuationBasis: "Valuation Basis",
  employersAgentRepresentative: "Employer's Agent Representative", instructionDate: "Issue Date",
  instructionTime: "Issue Time", contractorRemarks: "Contractor's Remarks", receivedBy: "Received By",
  receivedDate: "Received Date", receivedTime: "Received Time", distribution: "Distribution",
  status: "Status",
};

const t = tokens;

function statusVariant(status) {
  switch (status) {
    case "Completed":   return "success";
    case "In Process":  return "info";
    case "Outstanding": return "warning";
    case "Cancelled":   return "danger";
    default:            return "default";
  }
}

function valuationVariant(basis) {
  switch (basis) {
    case "Saving":     return "success";
    case "New Rates":  return "warning";
    case "Day Works":  return "info";
    case "Admeasure":  return "primary";
    case "None":       return "default";
    default:           return "default";
  }
}

// ─── Page-specific icons ─────────────────────────────────────────────────────
const I = {
  add:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  save:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  cancel: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  view:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  edit:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  link:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
};

// ─── Inline pill showing a linked record reference ──────────────────────────
const LinkedRefPill = memo(function LinkedRefPill({ kind, code, subject, accent }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "8px 12px",
      background: t.color.surfaceAlt,
      border: `1px solid ${t.color.border}`,
      borderRadius: t.radius.sm,
      borderLeft: `3px solid ${accent || t.color.accent}`,
    }}>
      <span style={{
        fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
        textTransform: "uppercase", color: t.color.inkSubtle,
      }}>Linked {kind}</span>
      <span style={{ fontFamily: t.font.mono, fontSize: 12, fontWeight: 700, color: t.color.ink }}>
        {code}
      </span>
      {subject && (
        <span style={{ fontSize: 12, color: t.color.inkMuted }}>· {subject}</span>
      )}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function InstructionsSection({
  instructionForm,
  setInstructionForm,
  handleAddInstruction,
  instructions,
  currentProjectId,
  currentProject,
  rfis = [],
  variations = [],
  editingInstructionId,
  handleEditInstruction,
  handleDeleteInstruction,
  clearInstructionForm,
  handleCreateVariationFromInstruction,
  handleViewItem,
}) {
  const [formatOpen, setFormatOpen] = useState(false);
  const scanInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { name, fields:[keys] } | null
  const [scanError, setScanError] = useState("");
  const siConfig = useMemo(() => getInstructionConfig(currentProject), [currentProject]);

  const rows = useMemo(
    () => instructions.filter((item) => item.projectId === currentProjectId),
    [instructions, currentProjectId]
  );
  const projectRfis = rfis.filter((r) => r.projectId === currentProjectId);
  const projectVariations = variations.filter((v) => v.projectId === currentProjectId);
  const nextSiNumber = useMemo(
    () => suggestNextSiNumber(siConfig, rows, currentProject),
    [siConfig, rows, currentProject]
  );

  const autoProjectNumber = currentProject?.id || "";
  const autoContractNumber = currentProject?.contractNumber || currentProject?.contractNo || "";
  const autoContractDescription = currentProject?.name || currentProject?.contractDescription || "";
  const autoContractor = currentProject?.contractor || "";

  const updateForm = (field, value) =>
    setInstructionForm({ ...instructionForm, [field]: value, projectId: currentProjectId });

  // ── Upload & Scan — read a completed SI/VO document and pre-fill the form ────
  async function handleScanFile(e) {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setScanError("");
    setScanResult(null);
    setScanning(true);
    try {
      const found = await scanInstructionDocument(file);
      const keys = Object.keys(found);
      if (!keys.length) {
        setScanError("No instruction fields could be read from that document.");
        return;
      }
      // Merge scanned values in; auto-sourced contract fields still win on save.
      setInstructionForm((prev) => ({ ...prev, ...found, projectId: currentProjectId }));
      setScanResult({ name: file.name, fields: keys });
    } catch (err) {
      setScanError(err?.message || "Could not scan that document.");
    } finally {
      setScanning(false);
    }
  }

  const linkedRfi = projectRfis.find((r) => r.id === instructionForm.relatedRfiId);
  const linkedVariation = projectVariations.find((v) => v.id === instructionForm.relatedVariationId);

  // ── KPI counts ────────────────────────────────────────────────────────────
  const counts = useMemo(() => ({
    total:       rows.length,
    financial:   rows.filter((r) => r.financialImplications === "Yes").length,
    noVoYet:     rows.filter((r) => r.financialImplications === "Yes" && !r.relatedVariationId).length,
    asBuilt:     rows.filter((r) => r.asBuiltImplications === "Yes").length,
  }), [rows]);

  // ── Search / filter / sort ───────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [financialFilter, setFinancialFilter] = useState("All");
  const [valuationFilter, setValuationFilter] = useState("All");
  const [sortField, setSortField] = useState("instructionDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const debouncedSearch = useDebouncedValue(searchText, 180);

  const valuationOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.valuationBasis).filter(Boolean));
    return Array.from(set);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (statusFilter !== "All" && (row.status || "") !== statusFilter) return false;
      if (financialFilter !== "All" && (row.financialImplications || "No") !== financialFilter) return false;
      if (valuationFilter !== "All" && (row.valuationBasis || "") !== valuationFilter) return false;
      if (!needle) return true;
      const hay = [
        row.siNumber, row.subject, row.contractor, row.instructionText,
        row.issuedBy, row.status, row.drawingRevisionRef,
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });

    return [...filtered].sort((a, b) => {
      let av = a[sortField] || "";
      let bv = b[sortField] || "";
      if (sortField === "instructionDate") { av = a.instructionDate || a.date || ""; bv = b.instructionDate || b.date || ""; }
      return sortDirection === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rows, statusFilter, financialFilter, valuationFilter, debouncedSearch, sortField, sortDirection]);

  // ── Sign-off gate — issue an AI draft under a named engineer ────────────────
  const [signingId, setSigningId] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [templateRow, setTemplateRow] = useState(null);
  async function onSignOff(row) {
    if (!currentProjectId) return;
    if (!window.confirm(
      `Sign off site instruction ${row.siNumber || row.id}? ` +
      `This issues it as an official instruction recorded in your name.`
    )) return;
    setSigningId(row.id);
    try {
      await approveDocument({
        projectId: currentProjectId,
        documentType: "instruction",
        documentId: row.id,
      });
      // The live Firestore listener refreshes the row's approval state.
    } catch (e) {
      alert(e.message || "Sign-off failed.");
    } finally {
      setSigningId(null);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <section style={{ position: "relative", display: "grid", gap: 18, fontFamily: t.font.body }}>
      <BlueprintGrid />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 18 }}>

        {/* ───── PAGE HEADER ───── */}
        <Reveal delay={0}>
          <Card padded={false} style={{ borderRadius: 14, position: "relative" }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 40%, ${t.color.primary} 100%)`,
            }} />
            <div style={{ padding: "24px 26px", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Eyebrow>Contract Administration</Eyebrow>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                  <span style={{
                    fontFamily: t.font.mono, fontSize: 11, fontWeight: 600,
                    color: t.color.inkMuted, letterSpacing: "0.02em",
                  }}>{currentProjectId || "—"}</span>
                </div>
                <h1 style={{
                  margin: 0, fontFamily: t.font.display,
                  fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1,
                  color: t.color.ink,
                }}>Site Instructions</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5, maxWidth: 640 }}>
                  Record every instruction issued to the contractor — and convert any with
                  financial implications into a tracked variation.
                </p>
              </div>
             <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                ref={scanInputRef}
                type="file"
                accept=".pdf,image/*"
                style={{ display: "none" }}
                onChange={handleScanFile}
              />
              <Button
                variant="primary"
                disabled={scanning}
                onClick={() => scanInputRef.current && scanInputRef.current.click()}
              >
                {scanning ? "⏳ Scanning…" : "⬆ Upload & Scan"}
              </Button>
              <Button
                variant="subtle"
                disabled={rows.length === 0}
                onClick={() => printInstructionRegister(rows, siConfig, currentProject)}
              >
                ⎙ Instruction Register
              </Button>
              <Button variant="subtle" onClick={() => setFormatOpen(true)}>
                ⚙ Format
              </Button>
             </div>

 {editingInstructionId && (
                <Button variant="subtle" onClick={clearInstructionForm} icon={I.cancel}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </Card>
        </Reveal>

        {/* ───── KPI STRIP ───── */}
        <Reveal delay={120}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}>
            <Stat label="Total SIs"        rawValue={counts.total}     format={(v) => Math.round(v)} value={counts.total}     accent={t.color.primary} delay={80}  helper="Issued on this project" />
            <Stat label="Financial Impact" rawValue={counts.financial} format={(v) => Math.round(v)} value={counts.financial} accent={t.color.warning} delay={140} helper="Have commercial implications" />
            <Stat label="VO Pending"       rawValue={counts.noVoYet}   format={(v) => Math.round(v)} value={counts.noVoYet}   accent={t.color.accent}  delay={200} helper="Financial but no VO yet" />
            <Stat label="As-Built Impact"  rawValue={counts.asBuilt}   format={(v) => Math.round(v)} value={counts.asBuilt}   accent={t.color.info}    delay={260} helper="Affect the as-built record" />
          </div>
        </Reveal>

        {/* ───── SCAN REVIEW BANNER ───── */}
        {(scanResult || scanError) && (
          <Reveal delay={180}>
            {scanError ? (
              <div style={{
                padding: "12px 16px", borderRadius: 12,
                background: t.color.dangerSoft || "#fde8e8",
                border: `1px solid ${t.color.danger || "#dc2626"}`,
                color: t.color.danger || "#991b1b", fontSize: 13, fontWeight: 600,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
              }}>
                <span>⚠ {scanError}</span>
                <Button variant="subtle" onClick={() => setScanError("")}>Dismiss</Button>
              </div>
            ) : (
              <div style={{
                padding: "12px 16px", borderRadius: 12,
                background: t.color.surfaceAlt,
                border: `1px solid ${t.color.success || t.color.primary}`,
                borderLeft: `3px solid ${t.color.success || t.color.primary}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.color.ink }}>
                    ✓ Scanned {scanResult.fields.length} field{scanResult.fields.length === 1 ? "" : "s"} from {scanResult.name}
                  </span>
                  <Button variant="subtle" onClick={() => setScanResult(null)}>Dismiss</Button>
                </div>
                <p style={{ margin: "0 0 6px", fontSize: 12, color: t.color.inkMuted, lineHeight: 1.5 }}>
                  Review the values below — especially dates, ticks and amounts — then save. Nothing is committed until you do.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {scanResult.fields.map((k) => (
                    <Badge key={k} variant="success" size="sm">{SCAN_FIELD_LABELS[k] || k}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        )}

        {/* ───── FORM CARD ───── */}
        <Reveal delay={220}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow>{editingInstructionId ? "Editing record" : "New record"}</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: t.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
              }}>Instruction Details</h3>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              <FormGroupDivider>Identity</FormGroupDivider>

              <FormRow label="SI Number" helper={editingInstructionId ? "Auto-assigned reference (locked)" : "Auto-assigned — next in sequence for this project"}>
                <Input
                  mono
                  readOnly
                  value={editingInstructionId ? (instructionForm.siNumber || "") : nextSiNumber}
                  style={{ background: t.color.surfaceMuted || "#F3F4F6", cursor: "default" }}
                />
              </FormRow>

              <FormRow label="Project Number" helper="Pulled automatically from the Home section project details">
                <Input mono readOnly value={autoProjectNumber || instructionForm.projectNumber || ""} />
              </FormRow>

              <FormRow label="Contract Number" helper="Pulled automatically from the Home section contract details">
                <Input mono readOnly value={autoContractNumber || instructionForm.contractNumber || ""} />
              </FormRow>

              <FormRow label="Contract Description" helper="Pulled automatically from the Home section project details">
                <Textarea rows={2} readOnly value={autoContractDescription || instructionForm.contractDescription || ""} />
              </FormRow>

              <FormRow label="Contractor" helper="Pulled automatically from the Home section project details">
                <Input readOnly value={autoContractor || instructionForm.contractor || ""} />
              </FormRow>

              <FormRow label="Issued By" helper="Engineer or authorised person issuing the instruction">
                <Input value={instructionForm.issuedBy || ""} onChange={(e) => updateForm("issuedBy", e.target.value)} />
              </FormRow>

              <FormRow label="Status" helper="Register workflow status of this instruction">
                <Select value={instructionForm.status || ""} onChange={(e) => updateForm("status", e.target.value)}>
                  <option value="">Select</option>
                  {SI_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormRow>

              <FormGroupDivider>The Instruction</FormGroupDivider>

              <FormRow label="Subject" helper="Short subject line for the site instruction">
                <Input value={instructionForm.subject || ""} onChange={(e) => updateForm("subject", e.target.value)} />
              </FormRow>

              <FormRow label="Instruction" helper="Full instruction text issued to the contractor">
                <Textarea rows={4} value={instructionForm.instructionText || ""} onChange={(e) => updateForm("instructionText", e.target.value)} />
              </FormRow>

              <FormRow label="Applicable Drawing / Revision Reference" helper="Where applicable, include drawing number, description and revision number">
                <Input mono value={instructionForm.drawingRevisionRef || ""} onChange={(e) => updateForm("drawingRevisionRef", e.target.value)} />
              </FormRow>

              <FormRow label="Applicable Pay Items" helper="Enter relevant pay item references where applicable">
                <Textarea rows={2} value={instructionForm.applicablePayItems || ""} onChange={(e) => updateForm("applicablePayItems", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Commercial &amp; Technical Impact</FormGroupDivider>

              <FormRow label="As Built Implications" helper="Record whether the instruction has as-built implications">
                <Select value={instructionForm.asBuiltImplications || ""} onChange={(e) => updateForm("asBuiltImplications", e.target.value)}>
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </FormRow>

              <FormRow label="Financial Implications" helper="Tick if the instruction has financial implications">
                <Select value={instructionForm.financialImplications || "No"} onChange={(e) => updateForm("financialImplications", e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </Select>
              </FormRow>

              <FormRow label="Valuation Basis" helper="Select the applicable valuation basis shown on the instruction form">
                <Select value={instructionForm.valuationBasis || ""} onChange={(e) => updateForm("valuationBasis", e.target.value)}>
                  <option value="">Select</option>
                  <option value="Saving">Saving</option>
                  <option value="None">None</option>
                  <option value="Admeasure">Admeasure</option>
                  <option value="New Rates">New Rates</option>
                  <option value="Day Works">Day Works</option>
                </Select>
              </FormRow>

              <FormRow label="Employer's Agent Representative" helper="Person signing or issuing on behalf of the Employer's Agent">
                <Input value={instructionForm.employersAgentRepresentative || ""} onChange={(e) => updateForm("employersAgentRepresentative", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Dates &amp; Receipt</FormGroupDivider>

              <FormRow label="Instruction Issue Date" helper="Date on which the instruction was issued">
                <Input type="date" value={instructionForm.instructionDate || ""} onChange={(e) => updateForm("instructionDate", e.target.value)} />
              </FormRow>

              <FormRow label="Instruction Issue Time" helper="Time on which the instruction was issued">
                <Input type="time" value={instructionForm.instructionTime || ""} onChange={(e) => updateForm("instructionTime", e.target.value)} />
              </FormRow>

              <FormRow label="Contractor's Remarks" helper="Remarks captured from the contractor on receipt or review">
                <Textarea rows={2} value={instructionForm.contractorRemarks || ""} onChange={(e) => updateForm("contractorRemarks", e.target.value)} />
              </FormRow>

              <FormRow label="Instruction Received By" helper="Contractor's representative receiving the instruction">
                <Input value={instructionForm.receivedBy || ""} onChange={(e) => updateForm("receivedBy", e.target.value)} />
              </FormRow>

              <FormRow label="Received Date" helper="Date on which the instruction was received by the contractor">
                <Input type="date" value={instructionForm.receivedDate || ""} onChange={(e) => updateForm("receivedDate", e.target.value)} />
              </FormRow>

              <FormRow label="Received Time" helper="Time on which the instruction was received by the contractor">
                <Input type="time" value={instructionForm.receivedTime || ""} onChange={(e) => updateForm("receivedTime", e.target.value)} />
              </FormRow>

              <FormRow label="Distribution" helper="Distribution list or internal circulation note">
                <Input value={instructionForm.distribution || ""} onChange={(e) => updateForm("distribution", e.target.value)} />
              </FormRow>
<CustomFieldsInputs
  config={siConfig}
  values={instructionForm.customFields}
  onChange={(map) => updateForm("customFields", map)}
/>



              <FormGroupDivider>Linkage</FormGroupDivider>

              <FormRow label="Linked RFI" helper="Link this instruction to an existing RFI if applicable">
                <Select value={instructionForm.relatedRfiId || ""} onChange={(e) => updateForm("relatedRfiId", e.target.value)}>
                  <option value="">Not linked</option>
                  {projectRfis.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.rfiNo} — {r.subject}
                    </option>
                  ))}
                </Select>
              </FormRow>

              <FormRow label="Linked Variation Order" helper="Link this instruction to a VO if it results in a variation">
                <Select value={instructionForm.relatedVariationId || ""} onChange={(e) => updateForm("relatedVariationId", e.target.value)}>
                  <option value="">Not linked</option>
                  {projectVariations.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.variationNo} — {v.title || v.voDescription}
                    </option>
                  ))}
                </Select>
              </FormRow>

              {(linkedRfi || linkedVariation) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                  {linkedRfi && (
                    <LinkedRefPill kind="RFI" code={linkedRfi.rfiNo} subject={linkedRfi.subject} accent={t.color.info} />
                  )}
                  {linkedVariation && (
                    <LinkedRefPill kind="VO" code={linkedVariation.variationNo}
                      subject={linkedVariation.voDescription || linkedVariation.title}
                      accent={t.color.accent} />
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                <Button
                  variant="accent"
                  icon={editingInstructionId ? I.save : I.add}
                  onClick={() => {
                    setInstructionForm({
                      ...instructionForm,
                      projectId: currentProjectId,
                      projectNumber: autoProjectNumber || instructionForm.projectNumber || "",
                      contractNumber: autoContractNumber || instructionForm.contractNumber || "",
                      contractDescription: autoContractDescription || instructionForm.contractDescription || "",
                      contractor: autoContractor || instructionForm.contractor || "",
                    });
                    handleAddInstruction();
                  }}
                >
                  {editingInstructionId ? "Update Site Instruction" : "Save Site Instruction"}
                </Button>
                {editingInstructionId && (
                  <Button variant="subtle" onClick={clearInstructionForm} icon={I.cancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Reveal>

        {/* ───── TABLE CARD ───── */}
        <Reveal delay={300}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 16px" }}>
              <Eyebrow>Register</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: t.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
              }}>Issued Instructions</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: t.color.inkSubtle }}>
                Showing <strong style={{ fontFamily: t.font.mono, color: t.color.ink }}>{filteredRows.length}</strong> of{" "}
                <strong style={{ fontFamily: t.font.mono, color: t.color.ink }}>{rows.length}</strong> records
              </p>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              {rows.length > 0 && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 10, marginBottom: 16,
                }}>
                  <div style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: t.color.inkSubtle }}>Search</span>
                    <Input placeholder="SI number, subject, contractor…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                  </div>
                  {[
                    { label: "Status", value: statusFilter, onChange: setStatusFilter, options: [{ v: "All", l: "All statuses" }, ...SI_STATUS_OPTIONS.map((s) => ({ v: s, l: s }))] },
                    { label: "Financial", value: financialFilter, onChange: setFinancialFilter, options: [{ v: "All", l: "All" }, { v: "Yes", l: "Yes" }, { v: "No", l: "No" }] },
                    { label: "Valuation", value: valuationFilter, onChange: setValuationFilter, options: [{ v: "All", l: "All bases" }, ...valuationOptions.map((o) => ({ v: o, l: o }))] },
                    { label: "Sort By", value: sortField, onChange: setSortField, options: SORT_OPTIONS },
                    { label: "Direction", value: sortDirection, onChange: setSortDirection, options: [{ v: "asc", l: "Ascending" }, { v: "desc", l: "Descending" }] },
                  ].map((f) => (
                    <div key={f.label} style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: t.color.inkSubtle }}>{f.label}</span>
                      <Select value={f.value} onChange={(e) => f.onChange(e.target.value)}>
                        {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </Select>
                    </div>
                  ))}
                </div>
              )}
              {rows.length === 0 ? (
                <Empty
                  title="No site instructions yet"
                  message="Capture the first instruction using the form above. Items with financial implications can spawn a linked variation order."
                />
              ) : filteredRows.length === 0 ? (
                <Empty
                  title="No site instructions match the filter"
                  message="Adjust the search text or filters above to see more records."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>SI No</Th>
                      <Th>Date</Th>
                      <Th>Subject</Th>
                      <Th>Contractor</Th>
                      <Th align="center">Financial</Th>
                      <Th>Valuation</Th>
                      <Th>RFI</Th>
                      <Th>VO</Th>
                      <Th>Status</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const rowLinkedRfi = projectRfis.find((r) => r.id === row.relatedRfiId);
                      const rowLinkedVo = projectVariations.find((v) => v.id === row.relatedVariationId);
                      const showCreateVo = row.financialImplications === "Yes" && !row.relatedVariationId;
                      return (
                        <tr key={row.id}>
                          <Td mono weight={700}>{row.siNumber || "—"}</Td>
                          <Td mono>{row.instructionDate || row.date || "—"}</Td>
                          <Td>{row.subject || "—"}</Td>
                          <Td>{row.contractor || <span style={{ color: t.color.inkSubtle }}>—</span>}</Td>
                          <Td align="center">
                            {row.financialImplications === "Yes"
                              ? <Badge variant="warning" size="sm">Yes</Badge>
                              : <Badge variant="default" size="sm">No</Badge>}
                          </Td>
                          <Td>
                            {row.valuationBasis
                              ? <Badge variant={valuationVariant(row.valuationBasis)} size="sm">{row.valuationBasis}</Badge>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td mono>
                            {rowLinkedRfi
                              ? <span style={{ color: t.color.info, fontWeight: 700 }}>{rowLinkedRfi.rfiNo}</span>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td mono>
                            {rowLinkedVo
                              ? <span style={{ color: t.color.primary, fontWeight: 700 }}>{rowLinkedVo.variationNo}</span>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td>
                            {(() => {
                              const ab = approvalBadge(row);
                              return (
                                <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                                  {ab && <Badge variant={ab.variant} size="sm">{ab.label}</Badge>}
                                  {row.status
                                    ? <Badge variant={statusVariant(row.status)} size="sm">{row.status}</Badge>
                                    : (!ab && <span style={{ color: t.color.inkSubtle }}>—</span>)}
                                </div>
                              );
                            })()}
                          </Td>
                          <Td align="right">
                            <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              {requiresSignoff(row) && (
                                <Button size="sm" variant="accent" disabled={signingId === row.id} onClick={() => onSignOff(row)} icon={I.save}>
                                  {signingId === row.id ? "Signing…" : "Sign off"}
                                </Button>
                              )}
                              <Button size="sm" variant="success" onClick={() => handleViewItem(`Site Instruction ${row.siNumber || row.id}`, row)} icon={I.view}>
                                View
                              </Button>
<Button size="sm" variant="subtle" onClick={() => printInstruction(row, siConfig, currentProject)}>
  Print
</Button>
                              <Button size="sm" variant="subtle" onClick={() => setTemplateRow(row)}>
                                Template
                              </Button>
                              <Button size="sm" variant="subtle" onClick={() => setHistoryRow(row)}>
                                History
                              </Button>
                              <Button size="sm" variant="subtle" onClick={() => handleEditInstruction(row)} icon={I.edit}>
                                Edit
                              </Button>
                              <Button size="sm" variant="danger" onClick={() => handleDeleteInstruction(row.id)} icon={I.trash}>
                                Delete
                              </Button>
                              {showCreateVo && (
                                <Button size="sm" variant="accent" onClick={() => handleCreateVariationFromInstruction(row)} icon={I.link}>
                                  Create VO
                                </Button>
                              )}
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>
        </Reveal>

      </div>
{formatOpen && (
  <InstructionFormatPanel
    currentProject={currentProject}
    currentProjectId={currentProjectId}
    onClose={() => setFormatOpen(false)}
  />
)}

{historyRow && (
  <VersionHistory
    projectId={currentProjectId}
    collection="instructions"
    docId={historyRow.id}
    label={`SI ${historyRow.siNumber || historyRow.id}`}
    onClose={() => setHistoryRow(null)}
  />
)}

{templateRow && (
  <PopulateTemplateModal
    projectId={currentProjectId}
    project={currentProject}
    docType="instruction"
    row={templateRow}
    label={`SI ${templateRow.siNumber || templateRow.id}`}
    onClose={() => setTemplateRow(null)}
  />
)}

    </section>
  );
}

export default memo(InstructionsSection);
